/**
 * Conversations, kept BESIDE the graph and never inside it.
 *
 * The Ask channel produces text that is deliberately not part of the record:
 * it cannot search, so nothing it says has passed the citation gate, and
 * `engine/chat.ts` states the rule this module has to keep — speculation that
 * became a node "would be indistinguishable from reasoning the graph paid
 * for". Until now that rule was kept by throwing the transcript away on every
 * unmount, which also threw away twenty minutes of a reader's own work.
 *
 * So the transcripts persist, in their OWN KEYSPACE. `nv.chat.<sessionId>` is
 * not `nv.s.<sessionId>`, and that is the whole mechanism: every export, the
 * share link and the run manifest serialise the `Session` object, and no
 * conversation is reachable from it. The boundary is structural rather than a
 * rule someone has to remember at five call sites — the same argument
 * `demo.ts` makes for carrying no verification.
 *
 * IT IS A CACHE, NOT A DOCUMENT, and every decision below follows from that:
 *
 *  - **No migration, ever.** A blob that does not read as the current version
 *    is dropped rather than repaired. `persist.ts` cannot do that — a stored
 *    exploration is the user's work — but a conversation is recoverable by
 *    asking again, so a migration path here would be code carrying risk for
 *    something the app is allowed to lose.
 *  - **Caps, not budgets.** The session codec negotiates a budget and reports
 *    evictions, because losing an exploration is news. This trims oldest-first
 *    and says nothing, because losing the tail of a chat is not.
 *  - **A failed write is a no-op.** Quota belongs to the explorations; if the
 *    origin is full, the graph gets the room and the conversation does not.
 *  - **Orphans are collected.** A transcript whose exploration no longer
 *    exists is deleted on the next boot, so "forget this exploration" cannot
 *    leave its conversation on disk.
 *
 * And it is PRIVATE TO WHOEVER ASKED. A key names its owner as well as its
 * exploration, because one browser profile can hold more than one reader — see
 * `ConversationOwner` below, and D330.
 *
 * Only COMPLETED turns are stored. A turn still streaming, or one that failed,
 * is transient by definition, and writing a half-answer down would give the
 * next session a record of something the model never finished saying.
 */

/**
 * What one turn was about, when the conversation ranges over more than one
 * thing — the feed conversation does, where every earlier thread was pinned.
 *
 * The label is a COPY, `RunMove.label`'s rule: the node may be pruned or
 * rewritten long before anyone reads this back, and a turn that cannot say what
 * it was about is not a record. The id stays so a subject still on the graph is
 * one press away.
 */
export interface StoredAbout {
  kind: 'node' | 'graph'
  /** Absent for a graph turn, which names no node. */
  id?: string
  label: string
}

/** One question and its finished answer. Deliberately narrower than the
 *  engine's `ChatTurn`, which also carries the live and error states this
 *  module refuses to persist. */
export interface StoredTurn {
  id: string
  question: string
  answer: string
  /** When it was asked, ms. Lets the feed interleave a restored conversation
   *  with the session's own pass record, which carries real timestamps. Absent
   *  on turns stored before the feed existed — readers fall back gracefully. */
  at?: number
  /** See `StoredAbout`. Absent on the pinned threads, whose key already names
   *  the one subject, and on turns from before subjects were recorded. */
  about?: StoredAbout
  /** A turn the MODEL started — the narrator speaking after a pass, with no
   *  question above it. The one shape where an empty `question` is a valid
   *  turn rather than a malformed one; see the parse below. */
  voice?: true
  /**
   * Passes the reply proposed, as data — a kind, a node id, a few words of
   * why. LOOSE strings here on purpose: this module imports nothing, so the
   * verb vocabulary is not its to know, and the surface that renders a chip
   * re-validates every row against the app's own tables before anything can
   * be pressed (`ui/actPress.ts`). Nothing stored here can spend.
   */
  acts?: Array<{ kind: string; node: string; why?: string }>
}

/** Which conversation: the whole graph, one node, or one candidate pair. The
 *  same string `AskPanel` has always keyed its reset on. */
export type ThreadKey = string

interface Blob {
  v: number
  threads: Record<ThreadKey, { turns: StoredTurn[]; at: number }>
}

/** Bumping this DISCARDS every stored conversation, by design — see the head
 *  of this file. It is not a schema in `persist.ts`'s sense and owes no
 *  migration entry. */
const V = 1

import {
  forgottenElsewhere,
  noteWorkingFrom,
  onForgottenElsewhere,
  reachedByForget,
} from './forgotten'
import {
  docIds,
  documentBackend,
  documentsHydrated,
  shelfOpen,
  storageHydrationFailure,
} from './docStore'
import { onRenamed } from './rename'

/** The device's shelf: every transcript written before owners existed, and every
 *  one the personal app writes now. */
const PREFIX = 'nv.chat.'
/**
 * The accounts' shelves, one per account, under a prefix that is NOT an
 * extension of the device's. `nv.chatu.` does not start with `nv.chat.`, so
 * nothing that scans the device's keyspace — the orphan sweep, Forget
 * everything — can reach an account's transcript, whatever the account is
 * called. The orphan sweep is the case that matters: it judges a transcript
 * against the device's library, and a shared graph is never in it, so a shared
 * workspace's conversations under the device's prefix were collected on the
 * next personal boot (A02).
 */
const ACCOUNT_PREFIX = 'nv.chatu.'
/** The library's own index, read here and never written. Named because it is a
 *  coupling: orphan collection asks whether the exploration a transcript belongs
 *  to still exists, and `persist.ts` owns that answer — one row per exploration
 *  this device holds, which is exactly the question. */
const INDEX_KEY = 'nv.index'
/** The legacy document bodies and the closing-tab checkpoint, read for the same
 *  question and for the same reason: an exploration whose only copy is still one
 *  of these exists, whatever the shelf says yet (see `storedIds`). */
const LEGACY_PREFIX = 'nv.s.'
const CHECKPOINT_KEY = 'nv.ckpt'
/**
 * The worked example's id, and the one exploration that has no document to be
 * an orphan of.
 *
 * `demo.ts` deliberately writes nothing to disk — it is authored, not the
 * user's work, and it is rebuilt on every blank boot. So the ordinary test
 * below ("is the exploration still there?") answers no for the demo forever,
 * and the sweep would take a conversation whose graph is about to come back on
 * screen under the same id. The demo is the first thing a new reader asks
 * questions about; losing that reading on reload is the exact papercut these
 * transcripts exist to stop. A literal rather than an import, for the reason
 * every other key here is one: this module reads what `persist.ts` owns and
 * imports nothing.
 */
const DEMO_ID = 's_demo'

/** Per thread. Long enough for a real sitting; the head is dropped first, so
 *  what survives is what was said most recently about the claim on screen. */
const MAX_TURNS = 40
/** Per exploration. A reader who has opened forty conversations in one graph
 *  is not coming back to the first of them. */
const MAX_THREADS = 24
/** Per exploration, in characters. Roughly a tenth of what one exploration may
 *  spend, because the graph is the thing worth the room. */
const MAX_CHARS = 120_000

/**
 * WHOSE conversations these are — the one fact a transcript key used to lack.
 *
 * A transcript is private to whoever asked, and the key named only the
 * exploration. That was enough while one browser profile meant one reader. A
 * shared workspace breaks it: two accounts signed in, one after the other, on
 * the same profile open the same graph under the same id, so the second read
 * `nv.chat.<id>` and was shown the first one's questions and the model's
 * answers to them (A01). The server scoped every model result by account; the
 * browser's cache was the leak.
 *
 * So the key names an owner too, of three kinds:
 *
 *  - `device` — the personal app, where one profile is one reader. Its keys are
 *    the ones every existing transcript already sits under, so nothing moves.
 *  - `account` — one signed-in actor in a shared workspace, with a shelf of its
 *    own under `ACCOUNT_PREFIX`.
 *  - `nobody` — signed out, or not yet known. Reads come back empty and writes
 *    are not made. It is the state a shared workspace holds before it knows who
 *    is there, so the failure of a call made late is silence rather than
 *    somebody else's transcript.
 *
 * This module is TOLD and never infers, `core/rename.ts`'s rule: it cannot see
 * a sign-in, and a guess at one is how a transcript reaches the wrong reader.
 * The default is `device` because the personal app has no sign-in to report.
 *
 * An UNSCOPED transcript — any `nv.chat.` key — is the device's, and no account
 * ever reads one. A shared workspace that wrote there before owners existed left
 * transcripts whose key never said who asked, and attributing them would hand
 * one account's words to whichever account signed in first, which is the defect
 * itself. They stay with the device, whose orphan sweep collects them once
 * their exploration is not in this device's library. The cost is a signed-in
 * reader's conversations from before the fix — a cache, by this file's own
 * rule, and recoverable by asking again.
 */
export type ConversationOwner =
  { kind: 'device' } | { kind: 'account'; id: string } | { kind: 'nobody' }

let owner: ConversationOwner = { kind: 'device' }

/**
 * How many times the owner has changed in this tab. Folded into every sender's
 * count — see `conversationEpoch` — so an answer started for one reader and
 * finishing after another arrived is dropped by the check that already drops
 * one finishing after a forget, rather than by a second mechanism every write
 * path would have to remember.
 */
let ownerChanges = 0

const ownerWatchers = new Set<(next: ConversationOwner) => void>()

/** Whose conversations are being read and written now. */
export function conversationOwner(): ConversationOwner {
  return owner
}

/**
 * The owner as one string, for the copies held above this module — the live
 * conversations in `ui/askLive.ts` are keyed by it. Distinct owners give
 * distinct strings, and none contains `/` or `:`, so a caller may join it to
 * other parts with either and split it off again at the first.
 */
export function conversationOwnerTag(o: ConversationOwner = owner): string {
  if (o.kind === 'device') return 'd'
  if (o.kind === 'nobody') return 'n'
  return `a.${encodeURIComponent(o.id)}`
}

/**
 * Say who is reading now. The shared workspace's shell calls this — `nobody`
 * before it knows, the account once signed in, `nobody` again on sign-out —
 * and the personal app never has to.
 *
 * A change bumps every sender's count, so nothing in flight for the last reader
 * can land in this one's shelf, and tells the subscribers, which hold this
 * reader's copies in memory and put the last one's down. Setting the owner who
 * is already there is nothing at all: no count moves and nobody is told, so a
 * shell that reports its state on every render costs nothing.
 *
 * An account with no id is `nobody`. A shell that has lost track of who is
 * signed in has not named anyone, and the one thing this must never do is give
 * every such reader one shared, unnamed shelf.
 */
export function setConversationOwner(next: ConversationOwner): void {
  const settled: ConversationOwner =
    next.kind === 'account'
      ? typeof next.id === 'string' && next.id
        ? { kind: 'account', id: next.id }
        : { kind: 'nobody' }
      : next.kind === 'device'
        ? { kind: 'device' }
        : { kind: 'nobody' }
  if (conversationOwnerTag(settled) === conversationOwnerTag(owner)) return
  owner = settled
  ownerChanges++
  for (const fn of ownerWatchers) fn(owner)
}

/** Told when the owner changes, after the change. See `setConversationOwner`. */
export function onConversationOwnerChanged(fn: (next: ConversationOwner) => void): () => void {
  ownerWatchers.add(fn)
  return () => ownerWatchers.delete(fn)
}

/** The prefix of the current owner's shelf, or null when nobody has one. */
function shelfPrefix(of: ConversationOwner = owner): string | null {
  if (of.kind === 'device') return PREFIX
  if (of.kind === 'nobody') return null
  // `.` is the seam between the account and the exploration, and
  // `encodeURIComponent` leaves dots alone, so they are escaped by hand: no
  // account's shelf can then be a prefix of another's.
  return `${ACCOUNT_PREFIX}${encodeURIComponent(of.id).replace(/\./g, '%2E')}.`
}

/** Where this exploration's transcripts sit for the current owner, or null
 *  when nobody's do. Resolved ONCE per operation, so a read and the write that
 *  follows it can never name two different shelves. */
function slotOf(sessionId: string, of: ConversationOwner = owner): string | null {
  const prefix = shelfPrefix(of)
  return prefix === null ? null : `${prefix}${sessionId}`
}

/**
 * How many times each exploration's conversations have been thrown away.
 *
 * A forget can land WHILE an answer is still arriving, and the write that
 * completes afterwards carries the session id it started with — so removing the
 * key is not enough on its own: the finished answer would put the conversation
 * straight back, and "forgotten" would mean "gone until the next boot sweep
 * notices again".
 *
 * A COUNTER rather than a tombstone, and the difference is not cosmetic. A
 * permanent "this id is forgotten" mark rests on ids never being reused, and
 * there is exactly one that is: `s_demo` is fixed, so forgetting the worked
 * example and opening it again would leave every later answer silently
 * unwritable for the rest of the tab. What actually needs refusing is a write
 * SCHEDULED BEFORE a forget and landing after it, which is a question about one
 * request rather than about an id: the sender takes the count when it starts and
 * offers it back at the end, and a write whose count is stale is dropped. A
 * conversation begun after the forget carries the new count and stores
 * normally.
 */
const epochs = new Map<string, number>()

/** The count a sender should carry. See `epochs`.
 *
 *  Also the moment this tab starts working from that exploration, for the half
 *  of the same question `epochs` cannot answer: a forget performed in ANOTHER
 *  tab, which module memory never hears about. See `core/forgotten.ts`. */
export function conversationEpoch(sessionId: string): number {
  noteWorkingFrom(sessionId)
  return currentEpoch(sessionId)
}

/**
 * The count a write is checked against: this exploration's forgets plus the
 * tab's owner changes. Both only ever go up, so the sum moves whenever either
 * does, and a write that began before a forget OR before the reader changed is
 * stale either way — the check never needs to know which. The owner's half is
 * global rather than per exploration because a sign-out changes whose shelf
 * EVERY exploration writes to, including ones no sender has touched yet.
 */
function currentEpoch(sessionId: string): number {
  return (epochs.get(sessionId) ?? 0) + ownerChanges
}

/**
 * Read directly rather than through `conversationEpoch`, which also takes this
 * tab's baseline. Forgetting is not starting to work from something, and that
 * incidental baseline was load-bearing in the wrong direction: the cross-tab
 * handler below fixes what a forget reaches by consulting those baselines, so an
 * id this very handler had just baselined read as spared for every subscriber
 * after it — the epoch bumped, the live copy kept.
 */
function bumpEpoch(sessionId: string) {
  epochs.set(sessionId, (epochs.get(sessionId) ?? 0) + 1)
}

/**
 * Whether an announcement reaches one exploration, asked by each subscriber
 * about the ids IT holds — which is why it is a predicate and not a set. This
 * module knows `epochs`; the live copies above it are keyed differently and are
 * not enumerable from here.
 *
 * A forget performed HERE reaches everything it names: it is this tab's own
 * press, and there is no baseline for it to be behind. One heard from ANOTHER
 * tab reaches only what this tab is actually behind on — see `reachedByForget`.
 * Passing it explicitly rather than letting subscribers infer it keeps the two
 * doors from depending on the order the UI happens to call them in.
 */
export type ForgetReach = (sessionId: string) => boolean
const REACHES_ALL: ForgetReach = () => true

/** Told when conversations are forgotten — one id, or null for all of them.
 *  The live copies live above this module (they hold a streaming turn, which
 *  is not a stored thing), so they cannot be reached from here; they subscribe
 *  instead, which keeps the dependency pointing the one way core allows.
 *
 *  `whose` is the owner whose shelf the forget removed, as
 *  `conversationOwnerTag` spells it, or null when that is not known — a forget
 *  heard from another tab, whose record names an exploration and no reader.
 *  A forget performed here knows: an acknowledged deletion can land after the
 *  reader who pressed it has signed out, and it is their copies that go, not the
 *  next reader's (D331). */
type ForgetWatcher = (sessionId: string | null, reaches: ForgetReach, whose: string | null) => void
const forgetWatchers = new Set<ForgetWatcher>()

export function onConversationsForgotten(fn: ForgetWatcher): () => void {
  forgetWatchers.add(fn)
  return () => forgetWatchers.delete(fn)
}

function announceForgotten(
  sessionId: string | null,
  reaches: ForgetReach,
  whose: string | null,
): void {
  for (const fn of forgetWatchers) fn(sessionId, reaches, whose)
}

/** The bytes at this slot, exactly as they stand. Read alongside the parse so a
 *  caller can say which bytes its copy came from. No slot — nobody's shelf —
 *  holds nothing. */
function rawThreads(slot: string | null): string | null {
  if (slot === null) return null
  try {
    return localStorage.getItem(slot)
  } catch {
    return null
  }
}

function read(sessionId: string): Blob {
  return parseThreads(rawThreads(slotOf(sessionId)))
}

function parseThreads(raw: string | null): Blob {
  const empty: Blob = { v: V, threads: {} }
  try {
    if (!raw) return empty
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return empty
    const blob = parsed as Partial<Blob>
    if (blob.v !== V || !blob.threads || typeof blob.threads !== 'object') return empty
    // Third-party text: this came off a store any script on the origin could
    // have written. Every field is checked rather than trusted, and anything
    // malformed is dropped instead of repaired.
    const threads: Blob['threads'] = {}
    for (const [key, entry] of Object.entries(blob.threads)) {
      if (!entry || typeof entry !== 'object') continue
      const { turns, at } = entry as { turns?: unknown; at?: unknown }
      if (!Array.isArray(turns)) continue
      const clean: StoredTurn[] = []
      for (const t of turns) {
        if (!t || typeof t !== 'object') continue
        const { id, question, answer, at, about, voice, acts } = t as Record<string, unknown>
        if (typeof id !== 'string' || typeof question !== 'string' || typeof answer !== 'string')
          continue
        // A narrator turn is the model speaking unprompted, so it has no
        // question BY SHAPE — for every other turn an empty question is still
        // the malformed record it always was.
        if (!answer) continue
        if (!question && voice !== true) continue
        // The optional fields are dropped ALONE when malformed — the turn
        // itself is sound text and survives. A turn is only as trustworthy as
        // its words; a timestamp or a subject chip is decoration on them.
        const kept: StoredTurn = { id, question, answer, ...(voice === true ? { voice } : {}) }
        if (typeof at === 'number' && Number.isFinite(at)) kept.at = at
        const a = about && typeof about === 'object' ? (about as Record<string, unknown>) : null
        if (
          a &&
          (a.kind === 'node' || a.kind === 'graph') &&
          typeof a.label === 'string' &&
          a.label
        ) {
          kept.about = {
            kind: a.kind,
            label: a.label,
            ...(typeof a.id === 'string' && a.id ? { id: a.id } : {}),
          }
        }
        if (Array.isArray(acts)) {
          const rows = acts
            .map((row) => {
              if (!row || typeof row !== 'object') return null
              const { kind, node, why } = row as Record<string, unknown>
              if (typeof kind !== 'string' || !kind) return null
              if (typeof node !== 'string' || !node) return null
              return {
                kind,
                node,
                ...(typeof why === 'string' && why ? { why } : {}),
              }
            })
            .filter((row) => row !== null)
          if (rows.length > 0) kept.acts = rows
        }
        clean.push(kept)
      }
      if (clean.length > 0) threads[key] = { turns: clean, at: typeof at === 'number' ? at : 0 }
    }
    return { v: V, threads }
  } catch {
    return empty
  }
}

/** Trim to the caps, oldest first at every level. Pure, so the trimming rule
 *  is testable without a store. */
function trim(blob: Blob): Blob {
  const threads: Blob['threads'] = {}
  for (const [key, entry] of Object.entries(blob.threads)) {
    if (entry.turns.length === 0) continue
    threads[key] = { turns: entry.turns.slice(-MAX_TURNS), at: entry.at }
  }
  // Newest-touched first, so the drops below take the coldest conversations.
  let keys = Object.keys(threads).sort((a, b) => threads[b]!.at - threads[a]!.at)
  if (keys.length > MAX_THREADS) {
    for (const key of keys.slice(MAX_THREADS)) delete threads[key]
    keys = keys.slice(0, MAX_THREADS)
  }
  // The character cap, applied by dropping whole conversations from the cold
  // end. Never by clipping an answer: half a sentence attributed to the model
  // is worse than an absent one.
  const size = () => JSON.stringify({ v: V, threads }).length
  while (keys.length > 1 && size() > MAX_CHARS) {
    const coldest = keys.pop()!
    delete threads[coldest]
  }
  // ONE conversation can exceed the cap on its own, so the loop above cannot be
  // where it ends. Forty turns of a 1600-token answer is a quarter of a million
  // characters, which is reachable by ordinary use — and stopping at the last
  // thread would leave the cap advertising a bound it does not hold, while the
  // blob took room meant for the explorations. So the survivor gives up its
  // OLDEST TURNS, head first, which is the same direction `MAX_TURNS` trims
  // and the same rule as above one level down: whole turns, never a clipped
  // answer.
  const last = keys[0]
  if (keys.length === 1 && last !== undefined) {
    const entry = threads[last]!
    // Never below one turn. A single answer larger than the whole cap is a
    // bound this module cannot honour without clipping, and it declines to
    // clip — the write is then simply refused by the store if it will not fit,
    // which is `write`'s own no-op.
    while (entry.turns.length > 1 && size() > MAX_CHARS) {
      entry.turns.shift()
    }
  }
  return { v: V, threads }
}

/** The text actually written, so a caller undoing itself can tell its own bytes
 *  from somebody else's. Null when nothing was left to write, or the store
 *  refused. */
/**
 * Write the blob back — but only over the bytes the caller's copy came from.
 *
 * `expect` is what stood at the key when the caller read. Compared HERE rather
 * than in the caller, on the near side of the write, because the `JSON.stringify`
 * below is the slowest thing between that read and this line. Checking earlier
 * would leave exactly that span open, which is the mistake `writeDoc` made by
 * asking before it serialised and `loadSession` made by asking before it parsed.
 *
 * A blob that has moved under us is another tab's newer transcript, and writing
 * over it is a LOST UPDATE the roll-back cannot mend: that check runs after the
 * write, so it can decline to remove somebody's newer bytes but cannot bring back
 * the ones this call displaced. Returning `false` lets the caller read again and
 * re-apply, which merges rather than clobbers — one thread is being set on a blob
 * of many.
 */
function write(slot: string, blob: Blob, expect: string | null): string | null | false {
  try {
    const trimmed = trim(blob)
    const empty = Object.keys(trimmed.threads).length === 0
    const text = empty ? '' : JSON.stringify(trimmed)
    if (localStorage.getItem(slot) !== expect) return false
    if (empty) {
      localStorage.removeItem(slot)
      return null
    }
    localStorage.setItem(slot, text)
    return text
  } catch {
    // Out of room, or no store at all. The explorations get the space; a
    // conversation that cannot be saved is simply not saved, and the reader
    // still has it on screen for as long as the panel is open.
    return null
  }
}

/** What was said in this conversation before, oldest first. */
export function readThread(sessionId: string, key: ThreadKey): StoredTurn[] {
  return read(sessionId).threads[key]?.turns ?? []
}

/** Replace one conversation. `now` is passed rather than read so the ordering
 *  a test asserts is the ordering the caller chose. */
export function writeThread(
  sessionId: string,
  key: ThreadKey,
  turns: StoredTurn[],
  now: number,
  /** The count this write was started under. Omitted by a write the reader
   *  performs directly — a Clear cannot be stale, it IS the latest word. */
  epoch?: number,
): void {
  setThread(sessionId, key, turns, now, epoch, false)
}

/**
 * How a put-back ended (D339). `stale`: the exploration was forgotten since the
 * Clear — here or in another tab — or the reader changed, which moves the same
 * count. `taken`: the shelf holds this conversation again, which only another
 * tab could have written. `restored` covers a shelf that could not take the
 * write too, the way an answer that cannot be saved is still an answer: the
 * turns are back on screen, and this store is a cache.
 */
export type PutBack = 'restored' | 'stale' | 'taken'

/**
 * Put back a conversation the reader cleared — the Clear's "Put it back"
 * (D339). Every guard an answer's own write passes, with the count the Clear was
 * pressed under as its epoch, so a thread can never come back onto an
 * exploration forgotten since — plus one of its own: it writes only into an
 * EMPTY key, judged on the same bytes it writes over. A key that holds turns
 * again was written by another tab after the Clear, and putting the old thread
 * back over it would be the lost update `write` exists to refuse.
 */
export function restoreThread(
  sessionId: string,
  key: ThreadKey,
  turns: StoredTurn[],
  now: number,
  epoch: number,
): PutBack {
  return setThread(sessionId, key, turns, now, epoch, true)
}

/** `writeThread` and `restoreThread`, one body, so the two cannot come to
 *  disagree about what a stale write is. */
function setThread(
  sessionId: string,
  key: ThreadKey,
  turns: StoredTurn[],
  now: number,
  epoch: number | undefined,
  onlyIntoEmpty: boolean,
): PutBack {
  // An answer that finishes after its exploration was forgotten must not put
  // the conversation back. See `epochs`. The second half is the same rule for a
  // forget performed in ANOTHER tab, where the count above never moved because
  // module memory is per tab; checked here rather than only on the announcement,
  // so it holds even when no `storage` event arrives.
  //
  // The same count also moves when the READER changes, so an answer begun for
  // one account and finishing after another signed in is dropped here too —
  // the slot below is the new reader's, and the words were the last one's.
  const stale = () =>
    (epoch !== undefined && epoch !== currentEpoch(sessionId)) || forgottenElsewhere(sessionId)
  if (stale()) return 'stale'
  // Nobody's shelf takes nothing: a reader nobody has named has nowhere private
  // to keep a conversation.
  const slot = slotOf(sessionId)
  if (slot === null) return 'restored'
  // ONE ATTEMPT. A write that finds bytes it did not read is simply not made.
  //
  // It retried once and merged the thread onto whatever was there now, which
  // saved the turn — and opened two rounds of findings, because a merge makes
  // somebody else's transcript part of what this call wrote, and the roll-back
  // below then takes the whole key on a byte match. Guarding the retry only moved
  // the question: the guard is a preflight, and the forget can land after it.
  //
  // So the retry is gone rather than guarded again. What prevented the data loss
  // was never the retry, it was the comparison inside `write`; the retry only
  // rescued this call's own turn. Dropping it costs a turn that lost a race and
  // buys back the roll-back's one assumption — that these bytes are this call's
  // alone — which is what made it safe to begin with.
  //
  // A turn that is not saved is what this shelf already does whenever it cannot
  // write. It is a cache, the answer is still on screen, and the question can be
  // asked again; another tab's transcript cannot be un-deleted.
  const before = rawThreads(slot)
  const blob = parseThreads(before)
  if (onlyIntoEmpty && blob.threads[key] !== undefined) return 'taken'
  if (turns.length === 0) delete blob.threads[key]
  else blob.threads[key] = { turns, at: now }
  const wrote = write(slot, blob, before)
  // Bytes that moved under the write are another tab's — `write` declined them.
  if (wrote === false) return onlyIntoEmpty ? 'taken' : 'restored'
  const text = wrote

  // Asked AGAIN now the write has landed, for the reason `writeDoc` gives: the
  // check and the write are two steps, and a forget arriving between them has
  // already removed this key — which the line above then recreates. Nothing
  // downstream cleans that up: the announcement only drops the live copies,
  // assuming the stored one is gone, and the boot sweep exempts `s_demo` by
  // name, so a transcript resurrected under the worked example's id would stay
  // there. Undone here instead.
  //
  // ONLY this call's own bytes, though. `s_demo` is reused, so between the write
  // above and this check the worked example can legitimately be forgotten and
  // replayed, and a conversation begun after that forget is somebody else's
  // valid work. Removing the key outright would delete it — undoing a write is
  // not licence to delete whatever happens to be there now.
  if (text !== null && forgottenElsewhere(sessionId)) {
    try {
      if (localStorage.getItem(slot) === text) localStorage.removeItem(slot)
    } catch {
      /* nothing to do — see `write` */
    }
    return 'stale'
  }
  return 'restored'
}

/** Forget one conversation — the Clear press. */
export function clearThread(sessionId: string, key: ThreadKey): void {
  writeThread(sessionId, key, [], 0)
}

/**
 * Follow a document that was renamed rather than switched.
 *
 * A paid pass on the worked example remints `s_demo` to a fresh id. It is the
 * same graph, still on screen, so the conversation about one of its claims is
 * still about the thing the reader is looking at — and left under the old name
 * it would drop out of the panel mid-sentence and sit on disk under an id the
 * demo will reuse.
 *
 * This module does NOT decide when that happened. An earlier version tried, by
 * watching for `s_demo` giving way to anything else, and it was wrong: opening a
 * saved exploration, replaying the demo and forking all look identical from
 * here, so it moved transcripts onto graphs they were not about. The store says
 * so now — see `core/rename.ts` — and this only obeys.
 *
 * Two details that are not obvious:
 *
 *  - **The epoch travels with the transcript.** A sender took its count under
 *    the old name and will offer it back under the new one, so leaving the count
 *    behind would make every in-flight answer read as stale and be dropped — a
 *    forget that nobody performed.
 *  - **Written before it is removed.** If the origin refuses the new key there
 *    is nothing to fall back on, so the old one has to still be there. A
 *    transcript that fails to move stays where it was and is collected later as
 *    an orphan, which loses a conversation the reader can ask again for; the
 *    other order loses it with no way back.
 *
 * The destination is always a freshly minted id, so nothing can be overwritten.
 * It moves the CURRENT reader's transcript, the one on screen; another
 * account's conversation about the worked example stays under the name that
 * account will find it by.
 */
export function renameConversations(from: string, to: string): void {
  if (from === to) return
  const carried = epochs.get(from)
  if (carried !== undefined) epochs.set(to, carried)
  const source = slotOf(from)
  const dest = slotOf(to)
  if (source === null || dest === null) return
  try {
    const blob = localStorage.getItem(source)
    if (blob === null) return
    localStorage.setItem(dest, blob)
    // COPIED, not moved, when the old name is the one this app REUSES.
    //
    // `s_demo` is fixed and origin-wide: every tab's worked example writes this
    // same key. A remint renames the document in ONE tab, and the others are
    // still sitting on the demo — so taking the shelf away leaves their
    // conversation live but doomed, whole on screen and gone on the next reload,
    // with nothing to tell them (a removal is not a forget and announces
    // nothing). The reader who spent the money gets their copy either way.
    //
    // `collectOrphanConversations` had already decided this, and the rename
    // contradicted it: the sweep refuses to collect exactly this key, on the
    // grounds that the demo comes back under this id and losing that reading is
    // the papercut these transcripts exist to stop. One half of the module
    // protecting a key the other half deletes is the disagreement, not the
    // leftover.
    //
    // The condition names the property rather than the id's role, so a genuine
    // one-to-one rename — a name only this tab could be holding — stays a move.
    if (from !== DEMO_ID) localStorage.removeItem(source)
  } catch {
    /* nothing to do — see `write` */
  }
}

// Registered here rather than at a call site, on the same argument the rest of
// this module makes: a rule that four callers each have to remember is a rule
// that drifts. Importing this module is what wires it up, and there is nothing
// to unsubscribe because the shelf lives as long as the tab.
onRenamed(({ from, to }) => renameConversations(from, to))

/** Forget every conversation of one exploration. Called where an exploration
 *  itself is forgotten: "forget" has to mean forgotten, and a transcript left
 *  behind would outlive the graph it discusses.
 *
 *  ONE reader's, and only theirs: another account's conversation about a shared
 *  graph is that account's to forget, and one reader's press must not reach into
 *  another's shelf any more than their eyes may. The current reader's unless the
 *  caller names the reader who pressed — which an acknowledged deletion does,
 *  because its acceptance can land after a change of reader, and resolving the
 *  shelf then would remove the new reader's transcript for the last one's press
 *  (D331). */
export function forgetConversations(sessionId: string, of: ConversationOwner = owner): void {
  bumpEpoch(sessionId)
  const slot = slotOf(sessionId, of)
  try {
    if (slot !== null) localStorage.removeItem(slot)
  } catch {
    /* nothing to do — see `write` */
  }
  announceForgotten(sessionId, REACHES_ALL, conversationOwnerTag(of))
}

// Another tab forgot something. The stored transcript is already gone — the
// store is shared — so what is left is this tab's idea of the count, which the
// senders read, and the live copies above this module, which subscribe to the
// announcement below. Deliberately NOT calling `forgetConversations`: that one
// records a forget durably, and a tab recording what another tab just told it
// would ratchet the count forever.
onForgottenElsewhere((sessionId) => {
  // Filtered, because a `storage` event is only a notification: the record it
  // is about has been readable here since the instant it was written, so this
  // tab may already have picked something up FROM the forgotten world in the
  // gap — a replayed worked example under its reused id, most of all. Those are
  // not behind, the durable log admits their writes, and dropping them here
  // would abort a question begun after the forget it is being blamed on.
  if (sessionId === null) {
    for (const id of [...epochs.keys()]) if (reachedByForget(id)) bumpEpoch(id)
  } else if (reachedByForget(sessionId)) {
    bumpEpoch(sessionId)
  }
  announceForgotten(sessionId, reachedByForget, null)
})

/**
 * Forget every conversation on this device — the "clear everything" door,
 * which promises everything and so has to name this shelf too.
 *
 * `forgotten` is what `forgetAll` in `persist.ts` just forgot. Where the page
 * has a shelf that is everything, and the sweep below takes the rest of this
 * keyspace with it — transcripts of explorations no row names any more. Where
 * it has none, the library showed only the explorations the page holds, the
 * graphs of every other one were left on a shelf the page could not read, and
 * their transcripts are left with them: a door that said "1" must not take
 * the conversations of a library it never listed.
 *
 * "Everything" is one reader's everything — theirs, never another account's,
 * for `forgetConversations`'s reason — and the current reader's unless the
 * caller names the one who pressed.
 */
export function forgetAllConversations(
  forgotten: readonly string[] = [],
  of: ConversationOwner = owner,
): void {
  if (!shelfOpen()) {
    for (const id of forgotten) forgetConversations(id, of)
    return
  }
  const prefix = shelfPrefix(of)
  const whose = conversationOwnerTag(of)
  // Nobody's shelf holds nothing to remove; the live copies still go.
  if (prefix === null) {
    announceForgotten(null, REACHES_ALL, whose)
    return
  }
  try {
    const mine: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(prefix)) mine.push(k)
    }
    // Counted up before the removal, for the reason `epochs` gives: an answer
    // still arriving belongs to one of these, and it would otherwise land
    // after the shelf was cleared.
    for (const k of mine) bumpEpoch(k.slice(prefix.length))
    for (const k of mine) localStorage.removeItem(k)
  } catch {
    /* nothing to do — see `write` */
  }
  announceForgotten(null, REACHES_ALL, whose)
}

/**
 * Whether an exploration exists, as a store that can answer says so.
 *
 * `absent` is a PROOF — a library read in full that does not hold it, or a
 * deletion its store accepted — and `unknown` is everything else: a shelf that
 * could not be read, a boot that never read it, a server that did not answer, a
 * list that stopped at a page. Only `absent` ever removes a transcript. The
 * personal boot used to read "not in this device's library" as absence, and a
 * shared graph never is, so opening the personal app collected every shared
 * conversation on the profile (A02). Missing data establishes nothing.
 */
export type Existence = 'present' | 'absent' | 'unknown'

/**
 * Drop transcripts whose exploration is gone — the device's shelf.
 *
 * The backstop under the explicit forget above, and the only cleanup that
 * covers the paths this module cannot see: a second tab clearing the library,
 * a document evicted by the budget, a session removed before this feature
 * existed. Run at boot, where a scan of the origin's keys costs nothing
 * anybody can perceive.
 *
 * The DEVICE's shelf only, and only while the device is the reader. Its
 * question — is this exploration in this device's library? — is the wrong one
 * for a shared graph, which never is, so an account's shelf is out of its reach
 * by prefix, and a boot that has named some other reader leaves the device's
 * shelf for the next boot that has not. Collecting late costs a little room;
 * collecting on the wrong library's word costs somebody's conversation.
 *
 * And only on a library that was actually READ: a shelf that opened is not one
 * that was hydrated, and a hydration that failed part-way has not listed what it
 * did not reach. Both answer `unknown` (`deviceExistence`).
 */
export async function collectOrphanConversations(): Promise<number> {
  if (owner.kind !== 'device') return 0
  const exists = await deviceExistence()
  // Asked again across the read: a sign-in landing while the shelf answered has
  // named a reader whose shelf this is not the question for.
  if (owner.kind !== 'device') return 0
  return sweepShelf(PREFIX, exists)
}

/**
 * Drop an ACCOUNT's transcripts whose graphs its workspace says are gone — the
 * same sweep, on the word of the store that holds that account's explorations
 * (D331). The hosted shell calls it with what its server has established, and
 * owes it the same discipline: `absent` only for a graph the server says does not
 * exist or that this account can no longer reach, never for one missing from a
 * list that may have stopped short (A06), never on a failed request.
 *
 * The signed-in account's shelf alone. A sweep for any other account — or with
 * nobody signed in — removes nothing: one reader's housekeeping must not reach
 * into another's shelf any more than their press may.
 */
export function collectAccountConversations(
  accountId: string,
  exists: (sessionId: string) => Existence,
): number {
  if (owner.kind !== 'account' || owner.id !== accountId) return 0
  return sweepShelf(shelfPrefix(owner)!, exists)
}

/**
 * What the device's library can say about an exploration. See `Existence`.
 *
 * Asked of the SHELF, at the moment of the sweep, as well as of the mirror and the
 * index. Those two are this tab's copies of the library, and other tabs can take
 * from them: a forget record dropped from the mirror, and a hydration rebuilding
 * the index from it, read as absence for a document whose deletion was refused and
 * which still stands on the shelf. What the shelf holds is not an inference.
 */
async function deviceExistence(): Promise<(sessionId: string) => Existence> {
  const unknown = () => 'unknown' as const
  if (!shelfOpen() || !documentsHydrated() || storageHydrationFailure() !== null) return unknown
  let shelved: string[]
  try {
    const kv = documentBackend()
    if (!kv) return unknown
    shelved = kv.ids ? await kv.ids() : (await kv.all()).map((row) => row.id)
  } catch {
    return unknown
  }
  if (!shelfOpen() || storageHydrationFailure() !== null) return unknown
  const stored = storedIds()
  if (stored === null) return unknown
  for (const id of shelved) stored.add(id)
  return (id) => (id === DEMO_ID || stored.has(id) ? 'present' : 'absent')
}

/**
 * Remove every transcript under `prefix` whose exploration is proven absent. The
 * count moves first and the live copies are told, for the reason `epochs` gives:
 * an answer still arriving for a graph deleted elsewhere would otherwise put its
 * conversation straight back. A verdict that throws has proven nothing.
 */
function sweepShelf(prefix: string, exists: (sessionId: string) => Existence): number {
  let dropped = 0
  try {
    const mine: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(prefix)) mine.push(k)
    }
    const whose = conversationOwnerTag()
    for (const k of mine) {
      const id = k.slice(prefix.length)
      let verdict: Existence
      try {
        verdict = exists(id)
      } catch {
        verdict = 'unknown'
      }
      if (verdict !== 'absent') continue
      bumpEpoch(id)
      localStorage.removeItem(k)
      dropped++
      announceForgotten(id, REACHES_ALL, whose)
    }
  } catch {
    /* nothing to do — see `write` */
  }
  return dropped
}

/** The hydrated shelf is authoritative. Its optional localStorage index can be
 * absent or stale after a refused metadata write, so it cannot establish that a
 * transcript is orphaned. Keep its legacy ids too until migration can settle —
 * the index rows, and the legacy bodies themselves, which a migration that
 * stopped part-way leaves with no row — and the closing-tab checkpoint's, whose
 * exploration may exist nowhere else until its recovery commits. */
function storedIds(): Set<string> | null {
  const ids = new Set(docIds())
  try {
    const rows = JSON.parse(localStorage.getItem(INDEX_KEY) ?? '[]') as unknown
    if (Array.isArray(rows))
      for (const row of rows) {
        const id = (row as { id?: unknown } | null)?.id
        if (typeof id === 'string') ids.add(id)
      }
  } catch {
    // A malformed optional index says nothing about the discovered shelf.
  }
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(LEGACY_PREFIX)) ids.add(k.slice(LEGACY_PREFIX.length))
    }
  } catch {
    // A key list that could not be read in full has not listed what it missed.
    return null
  }
  try {
    const checkpoint = JSON.parse(localStorage.getItem(CHECKPOINT_KEY) ?? 'null') as {
      id?: unknown
    } | null
    if (typeof checkpoint?.id === 'string') ids.add(checkpoint.id)
  } catch {
    // Unreadable checkpoint bytes may name anything, so nothing is proven gone
    // past them — the phantom sweep in `persist.ts` stops at them for the same
    // reason.
    return null
  }
  return ids
}
